import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api, unwrap, unwrapPage } from '@/api/client';
import type { components, operations } from '@/api/schema';

export type AlumnoResumen = components['schemas']['UserSummary'];
export type AlumnoDetalle = components['schemas']['UserDetail'];
export type LicenciaAlumno = components['schemas']['UserMembership'];
export type PagoAlumno = components['schemas']['UserPayment'];
export type SesionAlumno = components['schemas']['UserSession'];
export type OpcionLicencia = components['schemas']['MembershipOption'];
export type OtorgarBody = components['schemas']['UserMembershipGrantBody'];
export type FiltrosDirectorio = NonNullable<operations['getAdminUsers']['parameters']['query']>;

export const claveAlumno = (id: number) => ['alumnos', id] as const;

export function useDirectorio(query: FiltrosDirectorio) {
  return useQuery({
    queryKey: ['alumnos', 'lista', query],
    queryFn: () =>
      unwrapPage<AlumnoResumen[]>(api.GET('/api/v1/admin/users', { params: { query } })),
    placeholderData: keepPreviousData,
  });
}

export function useAlumno(id: number) {
  return useQuery({
    queryKey: claveAlumno(id),
    queryFn: () =>
      unwrap<AlumnoDetalle>(
        api.GET('/api/v1/admin/users/{id}', { params: { path: { id: String(id) } } }),
      ),
  });
}

export function useLicenciasAlumno(id: number) {
  return useQuery({
    queryKey: [...claveAlumno(id), 'licencias'],
    queryFn: () =>
      unwrap<LicenciaAlumno[]>(
        api.GET('/api/v1/admin/users/{id}/memberships', { params: { path: { id: String(id) } } }),
      ),
  });
}

export function usePagosAlumno(id: number) {
  return useQuery({
    queryKey: [...claveAlumno(id), 'pagos'],
    queryFn: () =>
      unwrap<PagoAlumno[]>(
        api.GET('/api/v1/admin/users/{id}/payments', { params: { path: { id: String(id) } } }),
      ),
  });
}

export function useSesionesAlumno(id: number) {
  return useQuery({
    queryKey: [...claveAlumno(id), 'sesiones'],
    queryFn: () =>
      unwrap<SesionAlumno[]>(
        api.GET('/api/v1/admin/users/{id}/sessions', { params: { path: { id: String(id) } } }),
      ),
  });
}

export function useOpcionesLicencia() {
  return useQuery({
    queryKey: ['licencias', 'opciones'],
    queryFn: () => unwrap<OpcionLicencia[]>(api.GET('/api/v1/admin/memberships/options')),
    staleTime: 5 * 60_000,
  });
}

/** "Hoy" en la Ciudad de México (AAAA-MM-DD), igual que la API. */
export function hoyMx(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(new Date());
}

// ------------------------------------------------------------------ borrar historial

export type HistorialBorrado = components['schemas']['UserHistoryDeleteResult'];
export type TipoVistas = NonNullable<
  operations['deleteAdminUsersByIdHistoryViews']['parameters']['query']
>['type'];

/** Tipos de vistas que se pueden borrar (mismo orden y claves que la API). */
export const TIPOS_VISTAS: Record<TipoVistas, string> = {
  todos: 'Todos los tipos',
  clases: 'Clases (reproducciones, respuestas de caso y quiz, reseñas)',
  flashcards: 'Flashcards',
  presentaciones: 'Presentaciones',
  manuales: 'Manuales (incluye el marcador de ENARM Plus)',
  pdfs: 'PDF',
  videos: 'Videos (casos contestados)',
  grabaciones: 'Grabaciones de clases en vivo',
};

const TABLAS: Record<string, string> = {
  class_progress: 'clases',
  content_views: 'vistas de contenido',
  user_content_cases: 'casos contestados en contenido',
  user_reading_positions: 'marcadores de lectura',
  live_recording_views: 'vistas de grabaciones',
  assessment_attempts: 'intentos',
  assessment_area_scores: 'puntajes por área',
  legacy_assessment_results: 'resultados históricos',
  legacy_assessment_area_scores: 'puntajes por área históricos',
  legacy_assessment_answers: 'respuestas históricas',
};

/** Texto del aviso tras borrar historial: total y desglose de las tablas con filas borradas. */
export function resumenBorrado(r: HistorialBorrado): { titulo: string; detalle?: string } {
  if (r.total === 0) return { titulo: 'No había historial que borrar.' };
  const detalle = r.deleted
    .filter((d) => d.rows > 0)
    .map((d) => `${TABLAS[d.table] ?? d.table}: ${d.rows}`)
    .join(' · ');
  return {
    titulo: `Se borraron ${r.total} ${r.total === 1 ? 'registro' : 'registros'}.`,
    detalle,
  };
}
